/**
 * Swaps the document's head over to the portal's identity while a portal route
 * is mounted, and puts it back on the way out.
 *
 * Doing this from React (rather than in public/index.html) keeps the portal's
 * manifest, fonts and theme colour off the admin dashboard entirely: an admin
 * never downloads Lato, and "Add to Home Screen" on /portal installs the driver
 * app — with `start_url: /portal` — not the dashboard.
 *
 * `installAtThisUrl` (the iPhone enrolment screen only) keeps every manifest
 * link out of the head so the Home Screen icon opens at the page's own URL.
 * Why that matters is written up at `useManifestLink` below.
 */
import { useEffect } from 'react';

const FONT_HREF = 'https://fonts.googleapis.com/css2?family=Lato:wght@400;700;900&family=Cormorant+Garamond:wght@500;600&display=swap';

const MANIFEST_HREF = '/manifest-portal.json';

const LINKS = [
  { rel: 'apple-touch-icon', href: '/portal-apple-touch-icon.png' },
  { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
  { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossOrigin: 'anonymous' },
  { rel: 'stylesheet', href: FONT_HREF },
];

const METAS = [
  // viewport-fit=cover so the safe-area insets the stylesheet relies on resolve.
  { name: 'viewport', content: 'width=device-width, initial-scale=1, viewport-fit=cover' },
  { name: 'theme-color', content: '#1a1a1a' },
  { name: 'apple-mobile-web-app-capable', content: 'yes' },
  { name: 'apple-mobile-web-app-status-bar-style', content: 'black-translucent' },
  { name: 'apple-mobile-web-app-title', content: 'PH Sofőr' },
];

const MARK = 'data-portal-chrome';

/**
 * The `<link rel="manifest">`, in an effect of its own so switching it off and
 * on again (the iPhone enrolment screen → "continue in the browser") leaves
 * the fonts, icon and meta tags alone.
 *
 * WHY THE iPHONE ENROLMENT SCREEN HAS NO MANIFEST
 * -----------------------------------------------
 * On iOS a Home Screen web app does not share cookies or storage with Safari.
 * The enrolment link is single-use, so whichever of the two spends it is the
 * one that ends up signed in. If Safari spends it, the icon the driver adds a
 * moment later opens signed out and the link is gone. The icon has to spend
 * it itself, on its first launch.
 *
 * That only works if the icon opens at /portal/e/:token. When the page links
 * a manifest, iOS uses the manifest's `start_url` (/portal: a signed-out gate
 * in the icon's empty storage). With no manifest linked, iOS falls back to the
 * URL of the page being added, which is the enrolment link itself. So while
 * that screen shows, every manifest link is taken out of the head (ours is
 * never added; anything else is detached) and put back when it goes. The
 * name and icon then come from `apple-mobile-web-app-title` and
 * `apple-touch-icon`, and `apple-mobile-web-app-capable` opens it standalone;
 * those are always set (METAS / LINKS above).
 *
 * Android is untouched: Chrome shares storage with the installed app, so
 * there the manifest (and its `start_url: /portal`) stays.
 */
function useManifestLink(enabled) {
  useEffect(() => {
    const { head } = document;
    if (!head) return undefined;

    if (enabled) {
      const link = document.createElement('link');
      link.rel = 'manifest';
      link.href = MANIFEST_HREF;
      link.setAttribute(MARK, 'true');
      head.appendChild(link);
      return () => link.parentNode && link.parentNode.removeChild(link);
    }

    // Someone else's manifest (none today; index.html links none) is detached
    // and put back exactly where it was.
    const detached = [...head.querySelectorAll('link[rel~="manifest"]')].map((node) => {
      const place = { node, parent: node.parentNode, next: node.nextSibling };
      node.parentNode.removeChild(node);
      return place;
    });
    return () => {
      detached.reverse().forEach(({ node, parent, next }) => {
        if (next && next.parentNode === parent) parent.insertBefore(node, next);
        else parent.appendChild(node);
      });
    };
  }, [enabled]);
}

export default function usePortalChrome(
  title = 'Pickup Hungary · Sofőr',
  { installAtThisUrl = false } = {},
) {
  useManifestLink(!installAtThisUrl);

  useEffect(() => {
    const { head } = document;
    if (!head) return undefined;

    const added = [];
    // name -> previous content, so the dashboard's own tags survive a visit.
    const restored = new Map();

    LINKS.forEach(({ rel, href, crossOrigin }) => {
      const link = document.createElement('link');
      link.rel = rel;
      link.href = href;
      if (crossOrigin) link.crossOrigin = crossOrigin;
      link.setAttribute(MARK, 'true');
      head.appendChild(link);
      added.push(link);
    });

    METAS.forEach(({ name, content }) => {
      const existing = head.querySelector(`meta[name="${name}"]:not([${MARK}])`);
      if (existing) {
        restored.set(existing, existing.getAttribute('content'));
        existing.setAttribute('content', content);
        return;
      }
      const meta = document.createElement('meta');
      meta.setAttribute('name', name);
      meta.setAttribute('content', content);
      meta.setAttribute(MARK, 'true');
      head.appendChild(meta);
      added.push(meta);
    });

    const previousTitle = document.title;
    document.title = title;

    return () => {
      added.forEach((node) => node.parentNode && node.parentNode.removeChild(node));
      restored.forEach((content, node) => {
        if (content === null) node.removeAttribute('content');
        else node.setAttribute('content', content);
      });
      document.title = previousTitle;
    };
  }, [title]);
}
